import { Card } from "@/components/Card";
import { FormattedText } from "@/components/FormattedText";
import { MatchedAdvisor } from "@/types/lead";
import { getInitials } from "@/utils/util";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import * as Tooltip from "@radix-ui/react-tooltip";
import { leadTopMatchTooltipViewed } from "@/utils/tracking";

interface AdvisorCardProps {
  advisor: MatchedAdvisor;
  leadId: number;
}

export const AdvisorCard = ({ advisor, leadId }: AdvisorCardProps) => {
  const handleTooltipOpenChange = (open: boolean) => {
    if (open && advisor.topMatch) {
      leadTopMatchTooltipViewed({
        lead_id: leadId,
        advisor_id: advisor.id,
        match_score: advisor.matchScore,
      });
    }
  };
  return (
    <Card className="relative flex flex-col h-full p-4 border rounded-xl shadow-sm border-gray-200">
      <div className="bg-gray-100 h-[3.5rem] w-full absolute top-0 left-0 flex items-start"></div>

      <div className="flex flex-col justify-between text-left flex-1">
        <div className="w-16 h-16 rounded-full z-1 border-4 border-white shadow-md bg-white border-primary-100 flex items-center justify-center">
          <span className="text-primary-500 font-semibold text-lg">
            {getInitials(advisor.name)}
          </span>
        </div>

        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-4 mb-2">
            <span className="font-bold text-lg leading-tight">
              {advisor.name}
            </span>
            {advisor.topMatch && (
              <span className="border border-primary-300 text-primary-500 text-xs px-4 py-1 rounded-full font-semibold flex items-center gap-2">
                TOP MATCH
                <Tooltip.Root onOpenChange={handleTooltipOpenChange}>
                  <Tooltip.Trigger asChild>
                    <button
                      type="button"
                      onClick={(e) => e.stopPropagation()}
                      className="text-primary-500 hover:text-primary-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-300 focus:ring-offset-1 rounded-full flex items-center justify-center"
                      aria-label="Info about top match"
                    >
                      <InfoCircledIcon className="w-3.5 h-3.5" />
                    </button>
                  </Tooltip.Trigger>
                  <Tooltip.Portal>
                    <Tooltip.Content
                      className="bg-white border border-gray-200 rounded-md shadow-lg p-3 max-w-sm z-50"
                      sideOffset={5}
                      side="top"
                    >
                      <div className="flex flex-col gap-2">
                        <div className="font-semibold text-gray-900 text-xs uppercase tracking-wide">
                          Top Match
                        </div>
                        <p className="text-sm text-gray-600 leading-relaxed">
                          This advisor is your best match based on an analysis
                          of your goals, profile, and advisory style
                          preferences.
                        </p>
                        <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                          <span className="text-xs font-medium text-gray-500">
                            Match Score:
                          </span>
                          <span className="text-sm font-bold text-primary-600">
                            {advisor.matchScore}%
                          </span>
                        </div>
                      </div>
                      <Tooltip.Arrow className="fill-white" />
                    </Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip.Root>
              </span>
            )}
          </div>
          <span className="text-gray-500 text-sm mb-4">
            {advisor.currentTitle}, {advisor.currentCompany}
          </span>
        </div>

        <div className="flex flex-col gap-1 mb-2">
          <span className="font-semibold">Bio</span>
          <div className="text-sm text-gray-600 whitespace-pre-line">
            <FormattedText
              text={
                advisor.bio.length > 300
                  ? `${advisor.bio.substring(
                      0,
                      advisor.bio.lastIndexOf(" ", 300)
                    )}...`
                  : advisor.bio
              }
            />
          </div>
        </div>

        {advisor.matchedGoals && advisor.matchedGoals.length > 0 && (
          <div className="flex flex-col gap-1">
            <span className="font-semibold">Matched Goals</span>
            <div className="text-sm text-gray-600">
              {advisor.matchedGoals.join(", ")}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};
